// Qayd sign-in layout: form on the left, product showcase on the right.
// Wraps every auth page (device keys, sign-in, OTP, password reset, register).
import { Suspense } from "react";
import { Outlet } from "lib/router";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { CircleCheck, ShieldCheck } from "lucide-react";
import { QaydLogo, PRODUCT } from "components/Brand";
import { CircleLoading } from "components/Loaders";

const glass = "border border-white/10 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.6)] backdrop-blur-md";

function ShipmentCard() {
  const progress = 62;
  return (
    <div className={`${glass} rounded-2xl bg-white/[0.06] p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[11px] text-white/45">CONT 414190 · 40′ HC</div>
          <div className="mt-1 text-[15px] font-semibold text-white">Houston → Jebel Ali</div>
          <div className="text-[11.5px] text-white/45">MSC Aurora · Voyage 642E</div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(125,125,240,0.2)] px-2.5 py-1 text-[11px] font-medium text-[#c9c9ff]">
          <span className="relative size-1.5">
            <span className="absolute inset-0 animate-[q-ping_1.8s_cubic-bezier(.2,.6,.4,1)_infinite] rounded-full bg-[#a5a5ff]" />
            <span className="absolute inset-0 rounded-full bg-[#a5a5ff]" />
          </span>
          On vessel
        </span>
      </div>
      <div className="relative mt-6 mb-2">
        <div className="h-1 rounded-full bg-white/10" />
        <div className="absolute top-0 left-0 h-1 rounded-full bg-gradient-to-r from-[#7d7df0] to-[#a5a5ff]" style={{ width: `${progress}%` }} />
        <div
          className="absolute -top-[5px] size-3.5 -translate-x-1/2 rounded-full border-2 border-[#0d0d14] bg-white shadow-[0_0_12px_rgba(165,165,255,0.9)]"
          style={{ left: `${progress}%` }}
        />
      </div>
      <div className="grid grid-cols-4 text-[10.5px]">
        {["Auction", "Loaded", "On vessel", "Arrived"].map((m, i) => (
          <div key={m} className={`${i === 0 ? "text-left" : i === 3 ? "text-right" : "text-center"} ${i <= 2 ? "text-white/70" : "text-white/35"}`}>
            {m}
          </div>
        ))}
      </div>
      <div className="mt-4 grid w-1/2 grid-cols-2 gap-3 border-t border-white/10 pt-4 text-[11px]">
        {[
          ["Vehicles", "4"],
          ["ETA", "02 Nov"],
        ].map(([k, v]) => (
          <div key={k}>
            <div className="text-white/40">{k}</div>
            <div className="mt-0.5 font-medium text-white/90">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandPanel() {
  return (
    <div className="relative hidden overflow-hidden bg-[#0b0b12] lg:block">
      <div
        aria-hidden
        className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.045)_1px,transparent_1px)] [background-size:40px_40px] [mask-image:radial-gradient(ellipse_75%_65%_at_55%_45%,#000_25%,transparent_75%)]"
      />
      <div aria-hidden className="absolute -top-[130px] -right-[100px] size-[560px] rounded-full bg-[rgba(91,91,214,0.3)] blur-[130px]" />
      <div aria-hidden className="absolute -bottom-[160px] -left-[100px] size-[420px] rounded-full bg-[rgba(16,185,129,0.12)] blur-[120px]" />

      <div className="relative flex h-full flex-col px-[8%] py-10">
        <QaydLogo tone="white" subtitle={PRODUCT.tagline} />
        <div className="mt-10 max-w-[480px]">
          <h2 className="m-0 text-[clamp(26px,2.3vw,40px)] leading-[1.15] font-semibold tracking-tight text-white">The ERP for growing businesses.</h2>
          <p className="m-0 mt-3 text-[14px] leading-relaxed text-white/55">Shipping, finance and operations in one workspace: fast, clear and built for daily work.</p>
        </div>

        <div className="relative my-8 flex flex-1 items-center">
          <div className="relative w-full max-w-[460px]">
            <div className="animate-[q-float_7s_ease-in-out_infinite,q-rise_.6s_cubic-bezier(.22,1,.36,1)_both]">
              <ShipmentCard />
            </div>
            <div className="absolute -bottom-14 w-[220px] animate-[q-float_9s_ease-in-out_infinite,q-rise_.6s_.15s_cubic-bezier(.22,1,.36,1)_both] lg:-right-3 xl:-right-14">
              <div className={`${glass} rounded-2xl bg-[rgba(21,21,31,0.92)] p-4`}>
                <div className="flex justify-between text-[11px] text-white/50">
                  <span>Received · October</span>
                  <span className="rounded-full bg-[rgba(52,211,153,0.15)] px-1.5 text-[#6ee7b7]">+11.8%</span>
                </div>
                <div className="mt-1.5 text-[19px] font-semibold tracking-tight text-white">AED 2.94M</div>
                <div className="mt-3 flex h-10 items-end gap-1">
                  {[38, 52, 44, 61, 57, 70, 66, 78, 72, 85, 81, 94].map((h, i) => (
                    <div key={i} className="flex-1 rounded-[2px] bg-[#7d7df0]" style={{ height: `${h}%`, opacity: 0.3 + i * 0.06 }} />
                  ))}
                </div>
              </div>
            </div>
            <div className={`absolute -top-10 lg:-right-2 xl:-right-10 animate-[q-rise_.6s_.3s_cubic-bezier(.22,1,.36,1)_both]`}>
              <div className={`${glass} flex items-center gap-2.5 rounded-xl bg-[rgba(21,21,31,0.95)] py-2 pr-3.5 pl-2.5`}>
                <span className="grid size-7 place-items-center rounded-full bg-[rgba(52,211,153,0.15)] text-[#6ee7b7]">
                  <CircleCheck className="size-4" />
                </span>
                <div className="text-[11.5px] leading-tight">
                  <div className="font-medium text-white">Payment voucher approved</div>
                  <div className="text-white/45">PV-20412 · just now</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
          {[
            ["Multi-company", "Own database per company"],
            ["87 modules", "Finance & accounting"],
            ["Real-time", "Tracking & notifications"],
          ].map(([a, b]) => (
            <div key={a}>
              <div className="text-[15px] font-semibold text-white">{a}</div>
              <div className="mt-0.5 text-[11.5px] text-white/45">{b}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AuthLayout() {
  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-[minmax(440px,1fr)_1.15fr]">
      <div className="flex min-h-screen flex-col px-6 py-8 sm:px-10">
        <QaydLogo />
        <div className={`mx-auto flex w-full max-w-[380px] flex-1 flex-col justify-center py-10 animate-[q-rise_.5s_cubic-bezier(.22,1,.36,1)_both]`}>
          {/* reCAPTCHA v3 is required by the sign-in call */}
          <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
            <Suspense fallback={<CircleLoading />}>
              <Outlet />
            </Suspense>
          </GoogleReCaptchaProvider>
        </div>
        <div className="flex flex-wrap justify-between gap-2 text-[11.5px] text-faint">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="size-3.5" /> Device verification & two-factor protected
          </span>
          <span>© 2026 {PRODUCT.name}</span>
        </div>
      </div>
      <BrandPanel />
    </div>
  );
}

/** Full-width public page (privacy policy, shared invoice, gate pass) with a slim Qayd header. */
export function PlainLayout() {
  return (
    <div className="min-h-screen bg-bg">
      <div className="flex h-14 items-center border-b border-line bg-panel px-4 sm:px-8">
        <QaydLogo size={26} />
      </div>
      <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}>
        <Suspense fallback={<CircleLoading />}>
          <Outlet />
        </Suspense>
      </GoogleReCaptchaProvider>
    </div>
  );
}
